import { useEffect, useState } from "react";
import { ApiRequest } from "GlobalFunctions";
import Box from "elements/Box";
import { Helmet } from 'react-helmet';

const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr.replace(' ', 'T'));
    return d.toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' });
};

function addBannerClass(html: string): string {
  return html.replace(
    /<!--BANNER_START-->([\s\S]*?)<!--BANNER_END-->/g,
    (_, inner) => {
      const patched = inner.replace(/^(<(?:div|a)\s)/, '$1class="eeh-banner" ');
      return `<!--BANNER_START-->${patched}<!--BANNER_END-->`;
    }
  );
}

function lazyLoadContentImages(html: string): string {
  return html.replace(/<img(\s)/gi, '<img loading="lazy" decoding="async"$1');
}

export default function BlogDetails(props: any) {
    const { id: blog_id } = props;
    const [details, setDetails] = useState<any>({});

    const loadData = async () => {
        ApiRequest({
            url: '/blog/details',
            method: 'GET',
            query: { blog_id },
            setResponse: (response: any) => {
                setDetails(response.data);
            }
        });
    };

    useEffect(() => {
        if (blog_id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const articleTitle = details.meta_title || details.title || 'Blog — Especialista en Heridas';
    const articleDesc = details.meta_description || 'Artículo del blog de Especialista en Heridas sobre curación de heridas y cuidado de salud a domicilio.';
    const articleUrl = details.alias ? `https://especialistaenheridas.cl/${details.alias}` : 'https://especialistaenheridas.cl/blog';

    const wordCount = details.content
        ? details.content.replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length
        : undefined;

    const articleJsonLd = details.title ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": details.title,
        "description": articleDesc,
        "url": articleUrl,
        "image": details.image || undefined,
        "datePublished": details.created_at || undefined,
        "dateModified": details.updated_at || details.created_at || undefined,
        "wordCount": wordCount || undefined,
        "inLanguage": "es-CL",
        "author": {
            "@type": "Organization",
            "name": "Especialista en Heridas",
            "url": "https://especialistaenheridas.cl"
        },
        "publisher": {
            "@type": "Organization",
            "name": "Especialista en Heridas",
            "url": "https://especialistaenheridas.cl",
            "logo": {
                "@type": "ImageObject",
                "url": "https://especialistaenheridas.cl/favicon/favicon-32x32.png"
            }
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl }
    } : null;

    const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://especialistaenheridas.cl" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://especialistaenheridas.cl/blog" },
            ...(details.title ? [{ "@type": "ListItem", "position": 3, "name": details.title, "item": articleUrl }] : [])
        ]
    };

    return (
        <>
            <Helmet>
                <title>{articleTitle}</title>
                <meta name="description" content={articleDesc} />
                {details.meta_keys && <meta name="keywords" content={details.meta_keys} />}

                <meta property="og:type" content="article" />
                <meta property="og:title" content={articleTitle} />
                <meta property="og:description" content={articleDesc} />
                <meta property="og:url" content={articleUrl} />
                {details.image && <meta property="og:image" content={details.image} />}

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={articleTitle} />
                <meta name="twitter:description" content={articleDesc} />
                {details.image && <meta name="twitter:image" content={details.image} />}

                <link rel="canonical" href={articleUrl} />
                {articleJsonLd && <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>}
                <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
            </Helmet>

            <div className="blog_details">
                <div className='blog_entry_header'>
                    <h2>{details.title}</h2>
                    <span className="blog_date">{formatDate(details.created_at)}</span>
                    {details.image &&
                        <img src={details.image} alt={details.title} className="blog_image" decoding="async" fetchPriority="high" />
                    }
                    <div className="blog_header_overlay"></div>
                </div>
                <Box className='blog_entry'>
                    <div className="blog_content_html" dangerouslySetInnerHTML={{ __html: lazyLoadContentImages(addBannerClass(details.content || '')) }} />
                </Box>
            </div>
        </>
    );
}
